import React, { useEffect, useRef, useState } from "react";
import { Phone, Home } from "lucide-react";
import { loadKestrel } from "./kestrel";
import { CONTACT } from "./data";

// Renders an iHomeFinder Kestrel widget into a div.
// - No `options` -> ihfKestrel.render() -> the full IDX search experience
//   (search form, results, and property detail pages) for the /idx page.
// - With options  -> e.g. { component: "listingSearchWidget" } for inline widgets.
//
// If the widget cannot load (script blocked, or the domain does not match the
// Website URL registered in the iHomeFinder Control Panel), a branded
// fallback with direct contact options is shown instead — never a blank box.
export const KestrelWidget = ({ options = null, minHeight = 480, testid = "kestrel-widget" }) => {
  const hostRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    loadKestrel()
      .then((kestrel) => {
        if (!alive || !hostRef.current) return;
        try {
          const rendered = options ? kestrel.render(options) : kestrel.render();
          hostRef.current.replaceChildren(rendered);
        } catch (err) {
          if (alive) setFailed(true);
        }
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (failed) {
    return (
      <div
        data-testid={`${testid}-fallback`}
        style={{ minHeight: 320 }}
        className="bg-[#F5F5F5] border border-[#E5E5E5] rounded-[20px] p-10 flex flex-col items-center justify-center text-center"
      >
        <div className="w-14 h-14 rounded-full bg-[#254369]/10 flex items-center justify-center mb-4">
          <Home className="text-[#254369]" size={26} />
        </div>
        <p className="font-heading font-bold text-xl text-[#2D2D37]">
          The home search is loading on our end
        </p>
        <p className="text-sm text-[#2D2D37]/60 mt-2 max-w-md">
          Our live MLS home search is momentarily unavailable. Call us and we
          will run the search for you, or try again in a moment.
        </p>
        <a
          href={`tel:${CONTACT.phoneTel}`}
          className="mt-5 inline-flex items-center gap-2 bg-[#254369] text-white rounded-lg px-6 py-3 font-medium hover:bg-[#2D2D37] transition-colors"
        >
          <Phone size={17} /> Call {CONTACT.phone}
        </a>
      </div>
    );
  }

  return <div ref={hostRef} data-testid={testid} style={{ minHeight }} className="ihf-kestrel-host" />;
};
